import React from 'react';
import { graphql } from 'gatsby';
import { ViewportImage } from '../viewport';
import { getSettingsClass } from '~/utils/helpers';

interface EdgeFeature {
  id: string;
  image: ImageField<ReusableImageField>[];
  title: string;
  description: string[];
  price: string;
  highlight?: boolean;
}

interface EdgeComputeComparisonProps {
  block: {
    settings: Settings;
    background_color: string;
    title: string;
    text: string;
    edge_features: EdgeFeature[];
    comparison_heading: string;
    comparison_ratio: string;
    comparison_items: {
      label: string;
      value: number;
      highlight?: boolean;
    }[];
  };
}

export const fragment = graphql`
  fragment EdgeComputeComparisonFragment on Block {
    settings {
      ...SettingsFragment
    }
    background_color
    title
    text
    edge_features {
      id
      image {
        ...ImageFieldFragment
      }
      title
      description
      price
      highlight
    }
    comparison_heading
    comparison_ratio
    comparison_items {
      label
      value
      highlight
    }
  }
`;

const EdgeComputeComparison: React.FC<EdgeComputeComparisonProps> = ({
  block: {
    settings,
    background_color,
    title,
    text,
    edge_features,
    comparison_heading,
    comparison_ratio,
    comparison_items,
  },
}) => {
  const settingsClass = getSettingsClass(settings);

  return (
    <div
      className={`mx-auto flex max-w-xl justify-center p-20 border-rounded-strong xl:max-w-lg lg:max-w-md md:max-w-sm md:p-12 sm:rounded-none sm:p-4 sm:py-16 ${settingsClass}`}
      style={{
        background: background_color,
      }}
    >
      <div className='text-white bg-gray-900 flex h-full w-full flex-col gap-4 text-[#fff] border-rounded-strong'>
        <div className='flex w-full flex-col items-center justify-center gap-4 text-center sm:text-left'>
          <h2
            className='mb-2 font-bold'
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
          <p dangerouslySetInnerHTML={{ __html: text }}></p>
        </div>
        <div className='mt-20 flex justify-center gap-4 md:flex-col md:items-center'>
          {edge_features.map(feature => {
            const [viewportImage] = feature.image ? feature.image : [];
            return (
              <div
                key={feature.id}
                className={`shadow-md relative flex flex-1 flex-col border border-solid bg-blue-dark-gradient p-12 border-rounded-strong md:w-full md:p-8 ${
                  feature.highlight
                    ? 'orange-rays bg-transparent-orange-gradient border-orange-accent'
                    : 'border-none'
                }`}
              >
                <ViewportImage
                  image={viewportImage}
                  className='mb-4 aspect-square w-fit flex-1 shrink-0 p-8 pl-0'
                />
                <div className='flex-1'>
                  <h4
                    className={`text-[2.5rem] font-semibold leading-snug ${feature.highlight ? 'text-orange-gradient' : ''}`}
                  >
                    {feature.title}
                  </h4>
                  <ul className='mt-2'>
                    {feature.description.map((desc, idx) => (
                      <li
                        key={idx}
                        className='text-[1.8rem] leading-loose'
                      >
                        {desc}
                      </li>
                    ))}
                  </ul>
                  <p
                    className={`mt-16 text-[6.0rem] font-bold ${feature.highlight ? 'text-orange-gradient' : ''}`}
                    dangerouslySetInnerHTML={{ __html: feature.price }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <div className='flex w-full flex-col items-center justify-between bg-blue-dark-gradient p-12 border-rounded-strong'>
          <h4
            className='text-2xl mt-8 text-[3.35rem] font-medium leading-tight'
            dangerouslySetInnerHTML={{ __html: comparison_heading }}
          />
          <div className='relative mt-8 flex w-full justify-between gap-8 md:flex-col-reverse md:gap-20'>
            <div className='flex w-2/3 flex-col gap-4'>
              <div className='flex flex-col items-start justify-between space-y-2'>
                Comparison
              </div>
              {comparison_items.map(item => (
                <div
                  key={item.label}
                  className={`mt-2 flex flex-col items-start justify-between space-y-4 ${
                    item.highlight ? 'text-orange-accent' : ''
                  }`}
                >
                  <span className='font-bold'>{item.label}</span>
                  <div
                    className={`h-8 w-full rounded-[40px] drop-shadow-sm ${
                      item.highlight ? 'bg-orange-to-blue' : 'bg-white-to-blue'
                    }`}
                    style={{
                      maxWidth: `${item.value}%`,
                      width: `100%`,
                    }}
                  />
                </div>
              ))}
            </div>
            <div className='-mt-12 flex-1 font-semibold md:mt-0'>
              <span
                className='my-0 py-0 text-[10rem] leading-none'
                dangerouslySetInnerHTML={{ __html: comparison_ratio }}
              />
              {/* <span className='block text-[4.1rem]'>cheaper</span> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EdgeComputeComparison;
